import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage, useBbPizza } from "../../api";
import { EmptyBlock, ErrorBlock, LevelBadge, LoadingBlock } from "../../components/primitives";
import { formatMs, formatStamp } from "../../format";
import type { BbPizzaState, BbPizzaTimeline } from "../../types";
import { Chip, VerdictBadge } from "../pipeline/shared";
import { KindChip, OutcomeChip, StepsLine } from "./bits";
import { baseItemId } from "./helpers";

interface TimelineEntry {
  ts: number;
  key: string;
  node: ReactNode;
}

/** Side panel with everything about one pizza (or BB line): order, stored pieces, what happened when. */
export function BbPizzaTimelineSheet({ itemId, onClose }: { itemId: string | null; onClose: () => void }) {
  const { t } = useLocalization();
  const query = useBbPizza(itemId);
  const data = query.data && itemId && baseItemId(query.data.item_id) === baseItemId(itemId) ? query.data : null;

  return (
    <Sheet open={!!itemId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-4 py-3 pr-10 text-left">
          <SheetTitle className="text-base">{t("monitoring.bb.timeline.title")}</SheetTitle>
          <SheetDescription className="text-xs">{t("monitoring.bb.timeline.description")}</SheetDescription>
          {itemId && <div className="break-all font-mono text-[11px] text-muted-foreground">{itemId}</div>}
        </SheetHeader>
        <div className="flex min-w-0 flex-col gap-4 p-4">
          {query.isError && <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />}
          {!data ? (
            !query.isError && (
              <>
                <LoadingBlock height={90} />
                <LoadingBlock height={240} />
              </>
            )
          ) : (
            <TimelineBody data={data} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TimelineBody({ data }: { data: BbPizzaTimeline }) {
  const { t } = useLocalization();
  const order = data.order;
  const multi = data.pizzas.length > 1;

  const entries: TimelineEntry[] = [];
  for (const hook of data.webhooks) {
    entries.push({
      ts: hook.ts,
      key: `w-${hook.id}`,
      node: (
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <KindChip kind={hook.kind} />
            <OutcomeChip outcome={hook.outcome} />
            {hook.event_type && <span className="font-mono text-[10px] text-muted-foreground">{hook.event_type}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {hook.kind === "item" && <StepsLine steps={hook.steps} />}
            {hook.kind === "item" && hook.item_id !== data.base_id && <span className="font-mono">{hook.item_id}</span>}
            {hook.kind === "status" && (
              <span className="font-mono">
                {hook.previous_status ? `${hook.previous_status} → ${hook.status ?? "?"}` : hook.status}
              </span>
            )}
            {hook.kind === "order" && hook.status && <span className="font-mono">{hook.status}</span>}
            {hook.camera && <span>{hook.camera}</span>}
            {hook.trigger_ms !== undefined && <span>{formatMs(hook.trigger_ms)}</span>}
            {hook.detail && <span className="break-all font-mono text-red-300/90">{hook.detail}</span>}
          </div>
        </div>
      ),
    });
  }
  for (const event of data.events) {
    if (!event.ts) continue;
    entries.push({
      ts: event.ts,
      key: `e-${event.id}`,
      node: (
        <div className="flex min-w-0 items-start gap-2">
          <LevelBadge level={event.level} />
          <div className="min-w-0">
            <div className="break-words text-xs">{event.message}</div>
            <div className="font-mono text-[10px] text-muted-foreground">
              {t("monitoring.bb.timeline.event")} · {event.source}.{event.code}
            </div>
          </div>
        </div>
      ),
    });
  }
  const milestone = (pizza: BbPizzaState, ts: number | null, label: string, extra?: ReactNode) => {
    if (!ts) return;
    entries.push({
      ts,
      key: `m-${pizza.id}-${label}`,
      node: (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-foreground/90">{label}</span>
          {multi && <span className="font-mono text-[10px] text-muted-foreground">{pizza.id}</span>}
          {extra}
        </div>
      ),
    });
  };
  for (const pizza of data.pizzas) {
    milestone(pizza, pizza.cooking_finished_at, t("monitoring.bb.timeline.milestone.cooked"));
    milestone(pizza, pizza.packing_finished_at, t("monitoring.bb.timeline.milestone.packed"));
    milestone(pizza, pizza.capture_at, t("monitoring.bb.timeline.milestone.captured"));
    milestone(pizza, pizza.evaluated_at, t("monitoring.bb.timeline.milestone.evaluated"),
      pizza.verdict ? <VerdictBadge verdict={pizza.verdict} /> : null);
  }
  entries.sort((a, b) => a.ts - b.ts);

  return (
    <>
      <section className="flex flex-col gap-1.5">
        <h4 className="text-xs font-semibold">{t("monitoring.bb.timeline.order")}</h4>
        {!order ? (
          <EmptyBlock>{t("monitoring.bb.timeline.noOrder")}</EmptyBlock>
        ) : (
          <div className="flex flex-col gap-1.5 rounded-md border px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {order.id && (
                <Link to={`/orders/${order.id}`} className="break-all font-mono text-[11px] hover:underline">
                  {order.id}
                </Link>
              )}
              {order.status && <Chip>{order.status}</Chip>}
              {order.is_test && <Chip tone="warn">test</Chip>}
            </div>
            <div className="flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
              {order.branch_name && <span>{order.branch_name}</span>}
              <span>{formatStamp(order.sent_at)}</span>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">{t("monitoring.bb.timeline.orderItems")}</div>
              <ul className="mt-0.5 flex flex-col gap-0.5">
                {order.items.map((item) => (
                  <li
                    key={item.id}
                    className={cn(
                      "flex flex-wrap items-baseline gap-x-2 text-[11px]",
                      baseItemId(item.id) === data.base_id ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    <span className="break-words">{item.name || "–"}</span>
                    {item.quantity !== null && item.quantity !== 1 && <span className="tabular-nums">× {item.quantity}</span>}
                    <span className="break-all font-mono text-[10px] text-muted-foreground">{item.id}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-1.5">
        <h4 className="text-xs font-semibold">{t("monitoring.bb.timeline.pieces")}</h4>
        {data.pizzas.length === 0 ? (
          <EmptyBlock>{t("monitoring.bb.timeline.noPieces")}</EmptyBlock>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {data.pizzas.map((pizza) => (
              <li key={pizza.id} className="flex flex-col gap-1 rounded-md border px-3 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Link to={`/pizzas/${pizza.id}`} className="break-words text-sm font-medium hover:underline">
                    {pizza.name || pizza.id}
                  </Link>
                  {pizza.verdict && <VerdictBadge verdict={pizza.verdict} />}
                </div>
                <div className="break-all font-mono text-[10px] text-muted-foreground">{pizza.id}</div>
                <div className="flex flex-wrap gap-1">
                  <Chip tone={pizza.cooking_finished ? "neutral" : "warn"}>
                    {t("monitoring.bb.timeline.cooked")} {pizza.cooking_finished === null ? "–" : pizza.cooking_finished ? "✓" : "✗"}
                  </Chip>
                  <Chip tone={pizza.packing_finished ? "neutral" : "warn"}>
                    {t("monitoring.bb.timeline.packed")} {pizza.packing_finished === null ? "–" : pizza.packing_finished ? "✓" : "✗"}
                  </Chip>
                  <Chip tone={pizza.capture_at ? "neutral" : "nok"}>
                    {pizza.capture_at
                      ? `${t("monitoring.bb.timeline.captured")} ${formatStamp(pizza.capture_at)}${pizza.capture_count > 1 ? ` · ${pizza.capture_count}×` : ""}`
                      : t("monitoring.bb.timeline.notCaptured")}
                  </Chip>
                  <Chip tone={pizza.image_received ? "neutral" : "warn"}>
                    {pizza.image_received ? t("monitoring.bb.timeline.photo") : t("monitoring.bb.timeline.noPhoto")}
                  </Chip>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-1.5">
        <h4 className="text-xs font-semibold">
          {t("monitoring.bb.timeline.events")}
          <span className="ml-2 font-normal text-muted-foreground">
            {t("monitoring.bb.timeline.webhooks", { count: data.webhooks.length })}
          </span>
        </h4>
        {entries.length === 0 ? (
          <EmptyBlock>{t("monitoring.bb.timeline.empty")}</EmptyBlock>
        ) : (
          <ol className="relative flex flex-col gap-2 border-l border-border/70 pl-4">
            {entries.map((entry) => (
              <li key={entry.key} className="relative">
                <span className="absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full bg-muted-foreground/60" aria-hidden />
                <div className="font-mono text-[10px] tabular-nums text-muted-foreground">{formatStamp(entry.ts)}</div>
                {entry.node}
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}
